/** Members — port of the original views.members: hours and days per person for the selected period. */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { fmtH, sum } from '@/domain/format';
import { periodLabel, periodRange } from '@/domain/periods';
import { useDataset } from '@/hooks/data';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { ActionsCell, AgoCell, ErrorState, IconButton, LoadingState, MemberLink, NoRecords, PeriodSelect, SearchBox, Topbar, type PeriodValue } from '@/ui/kit';
import { useAppActions } from '../actions';
import { memberRows } from './memberStats';
import { useDeleteMember } from './useDeleteMember';

export function MembersPage() {
  const ds = useDataset();
  const nav = useNavigate();
  const act = useAppActions();
  const deleteMember = useDeleteMember();
  const [query, setQuery] = useState('');
  const [period, setPeriod] = useState<PeriodValue>({ key: 'last7', from: '', to: '' });
  const [from, to] = periodRange(period.key, period.from, period.to);
  const list = useMemo(() => (ds.data ? memberRows(ds.data, query, [from, to]) : []), [ds.data, query, from, to]);
  const header = (
    <Topbar title="Members" sub="Hours and days worked per person for the selected period">
      <PermissionGate perm="members:write">
        <button className="btn primary" onClick={() => act.member()}>
          + Add Member
        </button>
      </PermissionGate>
    </Topbar>
  );
  if (ds.error) return (<>{header}<ErrorState error={ds.error} onRetry={ds.refetch} /></>);
  if (!ds.data) return (<>{header}<LoadingState /></>);
  const { projects, members } = ds.data;
  const pname = (id: string) => (id ? projects.find((p) => p.id === id)?.name || '—' : 'General');
  const count = `${list.length} of ${members.length} · ${list.filter((x) => x.hours > 0).length} worked in ${periodLabel(period.key, [from, to])} · ${fmtH(sum(list.map((x) => x.hours)))}`;
  return (
    <>
      {header}
      <div className="panel">
        <div className="panel-h">
          <div className="row grow">
            <SearchBox value={query} onChange={setQuery} placeholder="Search members" />
            <PeriodSelect value={period} onChange={setPeriod} />
          </div>
          <span className="hint">{count}</span>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>SR. No.</th>
                <th>Member</th>
                <th>Role</th>
                <th>Projects (period)</th>
                <th className="num">Days</th>
                <th className="num">Hours (period)</th>
                <th className="num">Hours (all time)</th>
                <th>Last worked</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length ? (
                list.map((x, i) => (
                  <tr key={x.member.id}>
                    <td>{i + 1}</td>
                    <td>
                      <MemberLink id={x.member.id} name={x.member.name} />
                    </td>
                    <td className="muted">{x.member.role || ''}</td>
                    <td>{x.projectIds.map(pname).join(', ') || <span className="muted">—</span>}</td>
                    <td className="num">{x.days || <span className="muted">0</span>}</td>
                    <td className="num">{x.hours ? fmtH(x.hours) : <span className="muted">0 h</span>}</td>
                    <td className="num">{fmtH(x.hoursAll)}</td>
                    <td>
                      <AgoCell iso={x.lastDate} />
                    </td>
                    <td>
                      <ActionsCell>
                        <IconButton icon="view" tip="View" onClick={() => nav(`/members/${encodeURIComponent(x.member.id)}`)} />
                        <PermissionGate perm="members:write">
                          <IconButton icon="edit" tip="Edit" onClick={() => act.member({ id: x.member.id })} />
                          <IconButton icon="del" tip="Delete" danger onClick={() => deleteMember(x.member)} />
                        </PermissionGate>
                      </ActionsCell>
                    </td>
                  </tr>
                ))
              ) : (
                <NoRecords cols={9} />
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
